import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NotFound = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Floating particles — reuse the same pattern as LoginBackground
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles: { x: number; y: number; r: number; dx: number; dy: number; alpha: number }[] = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 2 + 0.5,
        dx: (Math.random() - 0.5) * 0.4,
        dy: (Math.random() - 0.5) * 0.4,
        alpha: Math.random() * 0.5 + 0.1,
      });
    }

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,190,228,${p.alpha})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] relative overflow-hidden"
    >
      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      {/* Ambient blobs */}
      <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-neo-primary/25 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-[400px] h-[400px] bg-neo-matt/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-[#302b63]/40 rounded-full blur-[80px] pointer-events-none" />

      <div className="relative z-10 text-center px-6 max-w-lg mx-auto animate-fadeIn">

        {/* Giant ghost 404 */}
        <div className="relative mb-2 select-none leading-none">
          <span
            className="text-[8rem] sm:text-[8rem] font-poppins-700 text-transparent block"
            style={{ WebkitTextStroke: '1.5px rgba(212,190,228,0.15)' }}
          >
            404
          </span>
          {/* Glowing duplicate */}
          <span
            className="absolute inset-0 flex items-center justify-center text-[8rem] sm:text-[8rem] font-poppins-700 text-neo-primary/20 blur-[6px] select-none"
          >
            404
          </span>
        </div>

        {/* Glass card */}
        <div
          className="rounded-3xl p-8 sm:p-10 mb-8"
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          {/* NeoPass text logo */}
          <div className="flex justify-center mb-5">
            <div
              className="px-6 py-3 rounded-2xl"
             
            >
              <span className="text-white font-poppins-700 text-2xl tracking-wide">
                NeoPaas
              </span>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-poppins-600 text-white mb-3">
            {isRtl ? 'الصفحة غير موجودة' : 'Page Not Found'}
          </h1>
          <p className="text-sm sm:text-base font-poppins-400 leading-relaxed" style={{ color: 'rgba(212,190,228,0.6)' }}>
            {isRtl
              ? 'يبدو أن الصفحة التي تبحث عنها غير موجودة أو تم نقلها.'
              : "The page you're looking for doesn't exist or may have been moved."}
          </p>

          {/* Divider */}
          <div className="my-6 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(212,190,228,0.2), transparent)' }} />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-poppins-500 text-sm text-white transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
              }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {isRtl ? 'رجوع' : 'Go Back'}
            </button>

            <button
              onClick={() => navigate('/neopaas/portal/login')}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-poppins-500 text-sm text-white transition-all duration-200 hover:scale-105 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #441752 0%, #6b2d82 100%)',
                border: '1px solid rgba(212,190,228,0.25)',
                boxShadow: '0 4px 20px rgba(68,23,82,0.5)',
              }}
            >
              {isRtl ? 'الصفحة الرئيسية' : 'Back to Login'}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>

        {/* Footer hint */}
        <p className="text-xs font-poppins-400" style={{ color: 'rgba(212,190,228,0.3)' }}>
          {isRtl ? 'رمز الخطأ: 404 — لم يتم العثور على الصفحة' : 'Error code: 404 — Not Found'}
        </p>
      </div>
    </div>
  );
};

export default NotFound;
